import { CURRENT_ENVIRONMENT, APIM_GATEWAY_URLS } from "./config/env";
import API_ROUTES_V2 from "./api-routes-v2";
// ============================================================================
// Base API URL
// ============================================================================

const BASE_API_URL = `${APIM_GATEWAY_URLS[CURRENT_ENVIRONMENT]}`;

// ============================================================================
// Route Constants by Feature Area
// ============================================================================

export const EntityRoutes = {
  fetchEntity: `${BASE_API_URL}/entity/{id}`,
  fetchAllEntities: `${BASE_API_URL}/fetchentities`,
  updateEntity: `${BASE_API_URL}/{id}/update`,
  fetchEntityAudit: `${BASE_API_URL}/{id}/audit?page={page}`,
  fetchEntitiesAudit: `${BASE_API_URL}/entities/audit?page={page}`,
  fetchServiceAudit: `${BASE_API_URL}/{id}/services/audit?page={page}`,
  fetchEntityLsp: `${BASE_API_URL}/{id}/lsp`,
  updateEntityPlus: `${BASE_API_URL}/entity-plus-config/{id}/update`,
  fetchEntityPlusConfigAudit: `${BASE_API_URL}/{id}/entity_plus_config/audit?page={page}`,
  createEmployee: `${BASE_API_URL}/entities/{id}/employee`,
  employee: `${BASE_API_URL}/entities/employee/{id}`,
} as const;

export const AddressRoutes = {
  fetchAddresses: `${BASE_API_URL}/{id}/addresses`,
  addAddress: `${BASE_API_URL}/{id}/addresses/add`,
  deleteAddress: `${BASE_API_URL}/addresses/delete/{id}`,
  updateAddress: `${BASE_API_URL}/addresses/{id}/update`,
  fetchAddressAudit: `${BASE_API_URL}/address/{id}/audit?page={page}&objectType={objectType}`,
} as const;

export const LocaleRoutes = {
  fetchStates: `${BASE_API_URL}/locale/states?country={country}`,
  fetchCities: `${BASE_API_URL}/locale/cities?country={country}&state={state}`,
  fetchHolidays: `${BASE_API_URL}/holidays?country={country}&year={year}`,
} as const;

export const PersonnelRoutes = {
  fetchPersonnel: `${BASE_API_URL}/{id}/personnel`,
  addPersonnel: `${BASE_API_URL}/{id}/personnel/add`,
  updatePersonnel: `${BASE_API_URL}/personnel/update/{id}`,
  deletePersonnel: `${BASE_API_URL}/personnel/delete/{id}`,
  fetchPersonnelAudit: `${BASE_API_URL}/{id}/personnel/audit?page={page}`,
} as const;

export const ShareholderRoutes = {
  fetchShareholders: `${BASE_API_URL}/{id}/shareholders`,
  addShareholder: `${BASE_API_URL}/{id}/shareholders/add`,
  updateShareholder: `${BASE_API_URL}/shareholder/update/{id}`,
  fetchShareholder: `${BASE_API_URL}/shareholders/{id}`,
  fetchShareholdersAudit: `${BASE_API_URL}/shareholders/{id}/audit?page={page}&objectType={objectType}`,
  fetchShareholderRegistry: `${BASE_API_URL}/{id}/registry?page={page}&pageSize={pageSize}`,
  addShareholderRegistry: `${BASE_API_URL}/{shareholder_id}/share_registry/add`,
  updateShareholderRegistry: `${BASE_API_URL}/share_registry/{id}/update`,
  fetchShareholderRegistryAudit: `${BASE_API_URL}/{id}/share_registry/audit?page={page}`,
} as const;

export const DirectorRoutes = {
  fetchDirectors: `${BASE_API_URL}/{id}/directors`,
  addDirector: `${BASE_API_URL}/{id}/directors/add`,
  updateDirector: `${BASE_API_URL}/directors/update/{id}`,
  deleteDirector: `${BASE_API_URL}/directors/delete/{id}`,
  fetchDirector: `${BASE_API_URL}/directors/{id}`,
  fetchDirectorsAudit: `${BASE_API_URL}/{id}/directors/audit?page={page}`,
} as const;

export const OfficerRoutes = {
  fetchOfficers: `${BASE_API_URL}/{entity_id}/officers?page={page}&pageSize={pageSize}&search={searchValue}&type={type}&sortBy={sortBy}&sortOrder={sortOrder}`,
  addOfficer: `${BASE_API_URL}/{id}/officers/add`,
  updateOfficer: `${BASE_API_URL}/officers/{id}/update`,
  fetchOfficer: `${BASE_API_URL}/officers/{id}`,
  fetchOfficerAudit: `${BASE_API_URL}/officers/{id}/audit?page={page}`,
} as const;

export const AuthRoutes = {
  login: `${BASE_API_URL}/login?key={code}`,
  verifyToken: `${BASE_API_URL}/verify-token`,
  logout: `${BASE_API_URL}/logout`,
} as const;

export const UserRoutes = {
  fetchUser: `${BASE_API_URL}/user`,
  fetchWorkosUser: `${BASE_API_URL}/user/{id}`,
  deleteUser: `${BASE_API_URL}/users/delete/{id}`,
  fetchUsers: `${BASE_API_URL}/users?page={page}&pageSize={pageSize}&role={role}&search={searchValue}&entity={entity}&service={service}`,
  updateUser: `${BASE_API_URL}/user/{id}/update`,
  addUserEntityAccess: `${BASE_API_URL}/users/entity/add-access`,
  fetchUserEntityAccess: `${BASE_API_URL}/users/entity-access/{id}`,
  fetchUserAuditEntityAccess: `${BASE_API_URL}/{id}/users/audit?page={page}`,
  removeUserEntityAccess: `${BASE_API_URL}/users/entity/delete-access`,
  disableNotification: `${BASE_API_URL}/user/config/disable-notification-for-public-messages`,
} as const;

export const LspRoutes = {
  onboarding: `${BASE_API_URL}/lsponboarding`,
  fetchLsps: `${BASE_API_URL}/fetchlsp`,
  fetchLsp: `${BASE_API_URL}/lsp/{id}`,
  updateLsp: `${BASE_API_URL}/lsp/{id}/update`,
  fetchLspsAudit: `${BASE_API_URL}/lsps/audits?page={page}`,
  fetchLspAudit: `${BASE_API_URL}/lsp/{id}/audit?page={page}`,
  fetchLspCustomerAudit: `${BASE_API_URL}/lsp/{id}/customer/audit?page={page}`,
} as const;

export const AccountManagerRoutes = {
  fetchAccountManagers: `${BASE_API_URL}/account-managers`,
  createAccountManager: `${BASE_API_URL}/account-managers`,
} as const;

export const ProcessRoutes = {
  fetchProcess: `${BASE_API_URL}/processes/{id}`,
  fetchMilestones: `${BASE_API_URL}/processes/{id}/milestones`,
  fetchMilestonesSiblings: `${BASE_API_URL}/milestones/{id}/siblings`,
  updateTasks: `${BASE_API_URL}/tasks/update/{id}`,
  fetchProcessType: `${BASE_API_URL}/process-templates`,
  addProcess: `${BASE_API_URL}/{id}/processes/add`,
  fetchProcessAudit: `${BASE_API_URL}/processes/{id}/audit?page={page}`,
  fetchUserProcesses: `${BASE_API_URL}/processes?page={page}&sortBy={sortBy}&sortOrder={sortOrder}&pageSize={pageSize}&status={status}&service={service}&search={search}`,
  fetchProcessStatusOverview: `${BASE_API_URL}/stats`,
  addComments: `${BASE_API_URL}/comments?id={id}&type={type}`,
  fetchComments: `${BASE_API_URL}/comments?id={id}&page={page}`,
} as const;

export const ConversationRoutes = {
  addMessages: `${BASE_API_URL}/messages/add?relatedId={relatedId}&relatedType={relatedType}`,
  fetchPublicMessages: `${BASE_API_URL}/{id}/messages/public`,
  fetchInternalMessages: `${BASE_API_URL}/{id}/messages/internal`,
  fetchConversations: `${BASE_API_URL}/conversations?isPublic={isPublic}&relatedId={relatedId}&relatedType={relatedType}`,
  deleteConversation: `${BASE_API_URL}/conversation/delete/{id}`,
} as const;

export const DocumentRoutes = {
  fetchDocuments: `${BASE_API_URL}/documents?type={type}&id={id}`,
  fetchDocumentFolders: `${BASE_API_URL}/{id}/document-folders`,
  fetchFiles: `${BASE_API_URL}/files/search`,
  fetchRequestDocuments: `${BASE_API_URL}/requests/{id}/documents?requestType={requestType}`,
  addDocumentAudit: `${BASE_API_URL}/attachments/{id}/audit?type={type}`,
  fetchAttachmentAudit: `${BASE_API_URL}/attachments/{id}/audit?page={page}&objectType={objectType}`,
  restoreDocuments: `${BASE_API_URL}/attachment/restore`,
  deleteDocuments: `${BASE_API_URL}/attachment?relatedId={relatedId}&relatedType={relatedType}`,
  fetchTemporarySasToken: `${BASE_API_URL}/{container}/sas-token`,
} as const;

export const RequestRoutes = {
  fetchRequests: `${BASE_API_URL}/requests?page={page}&pageSize={pageSize}&filter={filter}&requestType={requestType}&entity={entity}`,
  fetchRequest: `${BASE_API_URL}/requests/{id}`,
  addRequest: `${BASE_API_URL}/requests/add?requestType={requestType}`,
  updateRequestStatus: `${BASE_API_URL}/requests/{id}/status`,
} as const;

export const CalendarRoutes = {
  fetchEvents: `${BASE_API_URL}/events?entityId={id}&year={year}&month={month}&day={day}&eventType={eventType}&entity={entity}&service={service}`,
  fetchUpcomingEvents: `${BASE_API_URL}/events/upcoming`,
} as const;

export const SystemRoutes = {
  getServices: `${BASE_API_URL}/services`,
  onboarding: `${BASE_API_URL}/onboarding`,
  organizations: `${BASE_API_URL}/organizations`,
} as const;

// ============================================================================
// Structured API Routes (New Implementation)
// ============================================================================

export const ApiRoutes = {
  entity: EntityRoutes,
  address: AddressRoutes,
  locale: LocaleRoutes,
  personnel: PersonnelRoutes,
  shareholder: ShareholderRoutes,
  director: DirectorRoutes,
  officer: OfficerRoutes,
  auth: AuthRoutes,
  user: UserRoutes,
  lsp: LspRoutes,
  process: ProcessRoutes,
  conversation: ConversationRoutes,
  document: DocumentRoutes,
  request: RequestRoutes,
  calendar: CalendarRoutes,
  system: SystemRoutes,
} as const;

// ============================================================================
// Legacy API_ROUTES Object (For Backward Compatibility)
// ============================================================================

export const API_ROUTES = {
  // Entity Routes
  FETCH_ENTITY: EntityRoutes.fetchEntity,
  ALL_ENTITIES: EntityRoutes.fetchAllEntities,
  UPDATE_ENTITY: EntityRoutes.updateEntity,
  FETCH_ENTITY_AUDIT: EntityRoutes.fetchEntityAudit,
  FETCH_ENTITIES_AUDIT: EntityRoutes.fetchEntitiesAudit,
  FETCH_ENTITY_LSP: EntityRoutes.fetchEntityLsp,
  UPDATE_ENTITY_PLUS: EntityRoutes.updateEntityPlus,
  FETCH_ENTITY_PLUS_CONFIG_AUDIT: EntityRoutes.fetchEntityPlusConfigAudit,
  FETCH_SERVICE_AUDIT: EntityRoutes.fetchServiceAudit,
  CERATE_EMPLOYEE: EntityRoutes.createEmployee,
  FETCH_EMPLOYEE: EntityRoutes.employee,
  UPDATE_EMPLOYEE: EntityRoutes.employee,

  // Address Routes
  FETCH_ADDRESSES: AddressRoutes.fetchAddresses,
  ADD_ADDRESS: AddressRoutes.addAddress,
  DELETE_ADDRESS: AddressRoutes.deleteAddress,
  UPDATE_ADDRESS: AddressRoutes.updateAddress,
  FETCH_ADDRESS_AUDIT: AddressRoutes.fetchAddressAudit,

  // Locale Routes
  FETCH_STATES: LocaleRoutes.fetchStates,
  FETCH_CITIES: LocaleRoutes.fetchCities,
  FETCH_HOLIDAYS: LocaleRoutes.fetchHolidays,

  // Personnel Routes
  PERSONNEL: PersonnelRoutes.fetchPersonnel,
  ADD_PERSONNEL: PersonnelRoutes.addPersonnel,
  UPDATE_PERSONNEL: PersonnelRoutes.updatePersonnel,
  DELETE_PERSONNEL: PersonnelRoutes.deletePersonnel,
  FETCH_PERSONNEL_AUDIT: PersonnelRoutes.fetchPersonnelAudit,

  // Shareholder Routes
  SHARE_HOLDERS: ShareholderRoutes.fetchShareholders,
  ADD_SHARE_HOLDER: ShareholderRoutes.addShareholder,
  UPDATE_SHAREHOLDER: ShareholderRoutes.updateShareholder,
  FETCH_SHAREHOLDER: ShareholderRoutes.fetchShareholder,
  FETCH_SHAREHOLDERS_AUDIT: ShareholderRoutes.fetchShareholdersAudit,
  FETCH_SHAREHOLDER_REGISTRY: ShareholderRoutes.fetchShareholderRegistry,
  ADD_SHAREHOLDER_REGISTRY: ShareholderRoutes.addShareholderRegistry,
  UPDATE_SHAREHOLDER_REGISTRY: ShareholderRoutes.updateShareholderRegistry,
  FETCH_SHAREHOLDER_REGISTRY_AUDIT:
    ShareholderRoutes.fetchShareholderRegistryAudit,

  // Director Routes
  FETCH_DIRECTORS: DirectorRoutes.fetchDirectors,
  ADD_DIRECTOR: DirectorRoutes.addDirector,
  UPDATE_DIRECTOR: DirectorRoutes.updateDirector,
  DELETE_DIRECTOR: DirectorRoutes.deleteDirector,
  FETCH_DIRECTOR: DirectorRoutes.fetchDirector,
  FETCH_DIRECTORS_AUDIT: DirectorRoutes.fetchDirectorsAudit,

  // Officer Routes
  FETCH_OFFICERS: OfficerRoutes.fetchOfficers,
  ADD_OFFICER: OfficerRoutes.addOfficer,
  UPDATE_OFFICER: OfficerRoutes.updateOfficer,
  FETCH_OFFICER: OfficerRoutes.fetchOfficer,
  FETCH_OFFICER_AUDIT: OfficerRoutes.fetchOfficerAudit,

  // Auth Routes
  LOGIN: AuthRoutes.login,
  VERIFY_TOKEN: AuthRoutes.verifyToken,
  LOGOUT: AuthRoutes.logout,

  // User Routes
  FETCH_USER: UserRoutes.fetchUser,
  FETCH_WORKOS_USER: UserRoutes.fetchWorkosUser,
  DELETE_USER: UserRoutes.deleteUser,
  FETCH_USERS: UserRoutes.fetchUsers,
  UPDATE_USER: UserRoutes.updateUser,
  ADD_USER_ENTITY_ACCESS: UserRoutes.addUserEntityAccess,
  FETCH_USER_ENTITY_ACCESS: UserRoutes.fetchUserEntityAccess,
  FETCH_USER_AUDIT_ENTITY_ACCESS: UserRoutes.fetchUserAuditEntityAccess,
  REMOVE_USER_ENTITY_ACCESS: UserRoutes.removeUserEntityAccess,
  DISABLE_NOTIFICATION: UserRoutes.disableNotification,

  // LSP Routes
  LSPONBOARDING: LspRoutes.onboarding,
  FETCH_LSPS: LspRoutes.fetchLsps,
  FETCH_LSP: LspRoutes.fetchLsp,
  UPDATE_LSP: LspRoutes.updateLsp,
  FETCH_LSPS_AUDIT: LspRoutes.fetchLspsAudit,
  FETCHLSP_AUDIT: LspRoutes.fetchLspAudit,
  FETCH_LSP_CUSTOMER_AUDIT: LspRoutes.fetchLspCustomerAudit,

  // Account Manager Routes
  FETCH_ACCOUNT_MANAGERS: AccountManagerRoutes.fetchAccountManagers,
  CREATE_ACCOUNT_MANAGER: AccountManagerRoutes.createAccountManager,

  // Process Routes
  FETCH_PROCESS: ProcessRoutes.fetchProcess,
  FETCH_MILESTONES: ProcessRoutes.fetchMilestones,
  FETCH_MILESTONES_SIBLINGS: ProcessRoutes.fetchMilestonesSiblings,
  UPDATE_TASKS: ProcessRoutes.updateTasks,
  FETCH_PROCESS_TYPE: ProcessRoutes.fetchProcessType,
  ADD_PROCESS: ProcessRoutes.addProcess,
  FETCH_PROCESS_AUDIT: ProcessRoutes.fetchProcessAudit,
  FETCH_USER_PROCESSES: ProcessRoutes.fetchUserProcesses,
  PROCESS_STATUS_OVERVIEW: ProcessRoutes.fetchProcessStatusOverview,
  ADD_TASK_COMMENTS: ProcessRoutes.addComments,
  FETCH_TASK_COMMENTS: ProcessRoutes.fetchComments,

  // Conversation Routes
  ADD_MESSAGES: ConversationRoutes.addMessages,
  FETCH_PUBLIC_MESSAGES: ConversationRoutes.fetchPublicMessages,
  FETCH_INTERNAL_MESSAGES: ConversationRoutes.fetchInternalMessages,
  FETCH_CONVERSATIONS: ConversationRoutes.fetchConversations,
  DELETE_CONVERSATION: ConversationRoutes.deleteConversation,

  // Document Routes
  FETCH_DOCUMENTS: DocumentRoutes.fetchDocuments,
  FETCH_DOCUMENT_FOLDERS: DocumentRoutes.fetchDocumentFolders,
  FETCH_FILES: DocumentRoutes.fetchFiles,
  FETCH_REQUEST_DOCUMENTS: DocumentRoutes.fetchRequestDocuments,
  ADD_DOCUMENT_AUDIT: DocumentRoutes.addDocumentAudit,
  FETCH_ATTACHMENT_AUDIT: DocumentRoutes.fetchAttachmentAudit,
  DELETE_ATTACHMENT: DocumentRoutes.deleteDocuments,
  RESTORE_ATTACHMENT: DocumentRoutes.restoreDocuments,
  FETCH_TEMPORARY_TOKEN: DocumentRoutes.fetchTemporarySasToken,

  // Request Routes
  FETCH_REQUESTS: RequestRoutes.fetchRequests,
  FETCH_REQUEST: RequestRoutes.fetchRequest,
  ADD_REQUEST: RequestRoutes.addRequest,
  UPDATE_REQUEST_STATUS: RequestRoutes.updateRequestStatus,

  // Calendar Routes
  FETCH_EVENTS: CalendarRoutes.fetchEvents,
  FETCH_UPCOMING_EVENTS: CalendarRoutes.fetchUpcomingEvents,

  // System Routes
  GET_SERVICES: SystemRoutes.getServices,
  ONBOARDING: SystemRoutes.onboarding,
  ORGANIZATIONS: SystemRoutes.organizations,
} as const;

// ============================================================================
// Exports
// ============================================================================

const targetRoutes = API_ROUTES_V2;

export { CURRENT_ENVIRONMENT as ENV };
export default targetRoutes;
